import { MemberGrant } from "./menuData"

export type PermissionManagedMemberGrant = Extract<MemberGrant, "최고관리자" | "진료의" | "실장" | "데스크" | "치료실" | "수납전용">

export type MemberGrantPermissionGrantKey = "superAdmin" | "doctor" | "headDesk" | "desk" | "treatmentRoom" | "paymentOnly"
export type MemberGrantPermissionFeatureKey =
  | "statistics"
  | "claim"
  | "encounterTreatment"
  | "herbalPrescription"
  | "settlement"
  | "memoEdit"
  | "memoDelete"
  | "encounterHistory"
  | "payment"
  | "paymentEdit"

export interface MemberGrantPermissionGrantDefinition {
  key: MemberGrantPermissionGrantKey
  label: PermissionManagedMemberGrant
}

export interface MemberGrantPermissionFeatureDefinition {
  key: MemberGrantPermissionFeatureKey
  label: string
}

export interface MemberGrantPermissionRow {
  statistics: boolean
  claim: boolean
  encounterTreatment: boolean
  herbalPrescription: boolean
  settlement: boolean
  memoEdit: boolean
  memoDelete: boolean
  encounterHistory: boolean
  payment: boolean
  paymentEdit: boolean
}

export interface MemberGrantPermissionSettingsData {
  permissionsByGrant: Record<MemberGrantPermissionGrantKey, MemberGrantPermissionRow>
}

export interface MemberGrantPermissionUserLike {
  isSuperuser?: boolean | null
  memberGrant?: string | null
}

const MEMBER_GRANT_PERMISSION_DEVICE_OVERRIDES: Record<string, Partial<MemberGrantPermissionRow>> = {
  디바이스_일반: { encounterHistory: true },
  디바이스_링크: { encounterHistory: true },
  디바이스_미디어: { encounterHistory: true },
}

const MEMBER_GRANT_PERMISSION_FORCED_FALSE_KEYS = new Set<string>([
  "desk:statistics",
  "desk:claim",
  "treatmentRoom:statistics",
  "treatmentRoom:claim",
  "paymentOnly:statistics",
  "paymentOnly:claim",
  "paymentOnly:encounterTreatment",
  "paymentOnly:memoDelete",
  "paymentOnly:encounterHistory",
])

export interface MemberGrantGuideRow {
  grant: PermissionManagedMemberGrant
  canManageAccounts: boolean
  canViewStatistics: boolean
  canManageClaim: boolean
  canManageEncounterTreatment: boolean
  canViewHerbalPrescription: boolean
  canDeleteOthersMemo: boolean
  canEditOthersMemo: boolean
  canViewEncounterHistory: boolean
  canManageSettlement: boolean
  canManagePayment: boolean
}

export const MEMBER_GRANT_PERMISSION_SETTING_NAME = "memberGrantPermissionSettings"

export const MEMBER_GRANT_PERMISSION_GRANTS: MemberGrantPermissionGrantDefinition[] = [
  { key: "superAdmin", label: "최고관리자" },
  { key: "doctor", label: "진료의" },
  { key: "headDesk", label: "실장" },
  { key: "desk", label: "데스크" },
  { key: "treatmentRoom", label: "치료실" },
  { key: "paymentOnly", label: "수납전용" },
]

export const MEMBER_GRANT_PERMISSION_FEATURES: MemberGrantPermissionFeatureDefinition[] = [
  { key: "statistics", label: "통계" },
  { key: "claim", label: "청구" },
  { key: "encounterTreatment", label: "진료처방" },
  { key: "herbalPrescription", label: "한약처방" },
  { key: "memoDelete", label: "메모삭제" },
  { key: "memoEdit", label: "메모수정" },
  { key: "encounterHistory", label: "진료이력" },
  { key: "settlement", label: "결산" },
  { key: "payment", label: "수납" },
  { key: "paymentEdit", label: "수납수정" },
]

const MEMBER_GRANT_PERMISSION_GRANT_LABEL_TO_KEY: Record<PermissionManagedMemberGrant, MemberGrantPermissionGrantKey> = {
  최고관리자: "superAdmin",
  진료의: "doctor",
  실장: "headDesk",
  데스크: "desk",
  치료실: "treatmentRoom",
  수납전용: "paymentOnly",
}

const createMemberGrantPermissionRow = (enabled: boolean): MemberGrantPermissionRow =>
  MEMBER_GRANT_PERMISSION_FEATURES.reduce((acc, feature) => {
    acc[feature.key] = enabled
    return acc
  }, {} as MemberGrantPermissionRow)

const EMPTY_MEMBER_GRANT_PERMISSION_ROW = createMemberGrantPermissionRow(false)
const FULL_MEMBER_GRANT_PERMISSION_ROW = createMemberGrantPermissionRow(true)

const DEFAULT_MEMBER_GRANT_PERMISSION_SETTINGS: MemberGrantPermissionSettingsData = {
  permissionsByGrant: {
    superAdmin: {
      statistics: true,
      claim: true,
      encounterTreatment: true,
      herbalPrescription: true,
      settlement: true,
      memoEdit: true,
      memoDelete: true,
      encounterHistory: true,
      payment: true,
      paymentEdit: true,
    },
    doctor: {
      statistics: false,
      claim: false,
      encounterTreatment: true,
      herbalPrescription: true,
      settlement: true,
      memoEdit: false,
      memoDelete: false,
      encounterHistory: true,
      payment: true,
      paymentEdit: false,
    },
    headDesk: {
      statistics: true,
      claim: false,
      encounterTreatment: true,
      herbalPrescription: true,
      settlement: true,
      memoEdit: true,
      memoDelete: true,
      encounterHistory: true,
      payment: true,
      paymentEdit: true,
    },
    desk: {
      statistics: false,
      claim: false,
      encounterTreatment: true,
      herbalPrescription: true,
      settlement: true,
      memoEdit: true,
      memoDelete: false,
      encounterHistory: true,
      payment: true,
      paymentEdit: false,
    },
    treatmentRoom: {
      statistics: false,
      claim: false,
      encounterTreatment: false,
      herbalPrescription: true,
      settlement: true,
      memoEdit: false,
      memoDelete: false,
      encounterHistory: true,
      payment: true,
      paymentEdit: false,
    },
    paymentOnly: {
      statistics: false,
      claim: false,
      encounterTreatment: false,
      herbalPrescription: false,
      settlement: true,
      memoEdit: false,
      memoDelete: false,
      encounterHistory: false,
      payment: true,
      paymentEdit: false,
    },
  },
}

const normalizePermissionBoolean = (value: unknown, defaultValue: boolean) => {
  if (typeof value === "boolean") {
    return value
  }

  if (typeof value === "number") {
    return value !== 0
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase()
    if (["true", "1", "yes", "y", "on"].includes(normalized)) {
      return true
    }
    if (["false", "0", "no", "n", "off", ""].includes(normalized)) {
      return false
    }
  }

  return defaultValue
}

export const isMemberGrantPermissionForcedFalse = (grantKey: MemberGrantPermissionGrantKey, featureKey: MemberGrantPermissionFeatureKey) => {
  return MEMBER_GRANT_PERMISSION_FORCED_FALSE_KEYS.has(`${grantKey}:${featureKey}`)
}

export const createDefaultMemberGrantPermissionSettingsData = (): MemberGrantPermissionSettingsData => ({
  permissionsByGrant: MEMBER_GRANT_PERMISSION_GRANTS.reduce(
    (acc, grant) => {
      acc[grant.key] = { ...DEFAULT_MEMBER_GRANT_PERMISSION_SETTINGS.permissionsByGrant[grant.key] }
      return acc
    },
    {} as Record<MemberGrantPermissionGrantKey, MemberGrantPermissionRow>
  ),
})

export const normalizeMemberGrantPermissionSettingsData = (value: unknown): MemberGrantPermissionSettingsData => {
  const defaults = createDefaultMemberGrantPermissionSettingsData()

  if (!value || typeof value !== "object") {
    return defaults
  }

  const rawValue = value as Record<string, unknown>
  const rawPermissionsByGrant =
    rawValue.permissionsByGrant && typeof rawValue.permissionsByGrant === "object"
      ? (rawValue.permissionsByGrant as Record<string, unknown>)
      : rawValue

  return {
    permissionsByGrant: MEMBER_GRANT_PERMISSION_GRANTS.reduce(
      (acc, grant) => {
        const defaultRow = defaults.permissionsByGrant[grant.key]
        const rawRow = rawPermissionsByGrant[grant.key]
        const normalizedRow = rawRow && typeof rawRow === "object" ? (rawRow as Record<string, unknown>) : {}

        const resolveFeatureValue = (featureKey: MemberGrantPermissionFeatureKey, defaultValue: boolean) => {
          if (isMemberGrantPermissionForcedFalse(grant.key, featureKey)) {
            return false
          }

          return normalizePermissionBoolean(normalizedRow[featureKey], defaultValue)
        }

        acc[grant.key] = {
          statistics: resolveFeatureValue("statistics", defaultRow.statistics),
          claim: resolveFeatureValue("claim", defaultRow.claim),
          encounterTreatment: resolveFeatureValue("encounterTreatment", defaultRow.encounterTreatment),
          herbalPrescription: resolveFeatureValue("herbalPrescription", defaultRow.herbalPrescription),
          settlement: resolveFeatureValue("settlement", defaultRow.settlement),
          memoEdit: resolveFeatureValue("memoEdit", defaultRow.memoEdit),
          memoDelete: resolveFeatureValue("memoDelete", defaultRow.memoDelete),
          encounterHistory: resolveFeatureValue("encounterHistory", defaultRow.encounterHistory),
          payment: resolveFeatureValue("payment", defaultRow.payment),
          paymentEdit: resolveFeatureValue("paymentEdit", defaultRow.paymentEdit),
        }

        return acc
      },
      {} as Record<MemberGrantPermissionGrantKey, MemberGrantPermissionRow>
    ),
  }
}

export const getMemberGrantPermissionGrantKey = (memberGrant?: string | null): MemberGrantPermissionGrantKey | null => {
  if (!memberGrant) {
    return null
  }

  return MEMBER_GRANT_PERMISSION_GRANT_LABEL_TO_KEY[memberGrant as PermissionManagedMemberGrant] ?? null
}

export const getMemberGrantPermissionRow = (
  settingsData: MemberGrantPermissionSettingsData | unknown,
  member?: MemberGrantPermissionUserLike | null
): MemberGrantPermissionRow => {
  if (member?.isSuperuser || member?.memberGrant === "최고관리자") {
    return { ...FULL_MEMBER_GRANT_PERMISSION_ROW }
  }

  const grantKey = getMemberGrantPermissionGrantKey(member?.memberGrant)
  if (!grantKey) {
    const deviceOverride = member?.memberGrant ? MEMBER_GRANT_PERMISSION_DEVICE_OVERRIDES[member.memberGrant] : null
    if (deviceOverride) {
      return {
        ...EMPTY_MEMBER_GRANT_PERMISSION_ROW,
        ...deviceOverride,
      }
    }

    return { ...EMPTY_MEMBER_GRANT_PERMISSION_ROW }
  }

  const normalizedSettings = normalizeMemberGrantPermissionSettingsData(settingsData)
  return {
    ...normalizedSettings.permissionsByGrant[grantKey],
  }
}

export const hasMemberGrantPermission = (
  settingsData: MemberGrantPermissionSettingsData | unknown,
  member: MemberGrantPermissionUserLike | null | undefined,
  featureKey: MemberGrantPermissionFeatureKey
) => {
  return getMemberGrantPermissionRow(settingsData, member)[featureKey]
}

export const createMemberGrantGuideRows = (
  settingsData: MemberGrantPermissionSettingsData = createDefaultMemberGrantPermissionSettingsData()
): MemberGrantGuideRow[] => {
  const normalizedSettings = normalizeMemberGrantPermissionSettingsData(settingsData)

  return MEMBER_GRANT_PERMISSION_GRANTS.map(grant => {
    const permissions = normalizedSettings.permissionsByGrant[grant.key]

    return {
      grant: grant.label,
      canManageAccounts: grant.key === "superAdmin",
      canViewStatistics: permissions.statistics,
      canManageClaim: permissions.claim,
      canManageEncounterTreatment: permissions.encounterTreatment,
      canViewHerbalPrescription: permissions.herbalPrescription,
      canDeleteOthersMemo: permissions.memoDelete,
      canEditOthersMemo: permissions.memoEdit,
      canViewEncounterHistory: permissions.encounterHistory,
      canManageSettlement: permissions.settlement,
      canManagePayment: permissions.payment,
    }
  })
}

export const MEMBER_GRANT_GUIDE_ROWS: MemberGrantGuideRow[] = createMemberGrantGuideRows()

